import { getCookie, setCookie, removeCookie } from '../utils/cookie';
import { request } from '../utils/http';
import {
  backendBaseUrl,
  searchApiTokenEndpoint,
  searchApiTokenRefreshEndpoint,
} from '../config';

const SEARCH_API_TOKEN_KEY = 'search_api_token';
const SEARCH_API_TOKEN_EXPIRES_AT_KEY = 'search_api_token_expires_at';
const SEARCH_API_TOKEN_TYPE_KEY = 'search_api_token_type';

// Buffer time before expiration to refresh token (5 minutes in milliseconds)
const REFRESH_BUFFER_TIME = 5 * 60 * 1000;

/**
 * Stores the Search API token and expiration
 * @param {string} token - The access token
 * @param {number} expiresAt - Timestamp when token expires
 * @param {string} tokenType - Token type (default: 'Bearer')
 */
export const setSearchApiToken = (token, expiresAt, tokenType = 'Bearer') => {
  setCookie(SEARCH_API_TOKEN_KEY, token);
  setCookie(SEARCH_API_TOKEN_EXPIRES_AT_KEY, expiresAt.toString());
  setCookie(SEARCH_API_TOKEN_TYPE_KEY, tokenType);
};

/**
 * Generates a new Search API token
 * @returns {Promise<[Error, Object]>} Tuple of [error, response]
 */
export const generateSearchApiToken = async () => {
  try {
    const [error, response] = await request({
      method: 'post',
      url: `${searchApiTokenEndpoint}`,
      baseURL: backendBaseUrl,
      data: {},
    })
      .then((data) => [null, data.data])
      .catch((errorData) => [errorData, null]);

    if (error || !response) {
      console.error('Failed to generate Search API token', error);
      return [error || new Error('Token generation failed'), null];
    }

    // Extract token data from response
    const { data } = response;
    if (!data || !data.accessToken) {
      return [new Error('Invalid token response structure'), null];
    }

    // Calculate expiration timestamp
    const expiresIn = data.expiresIn || 1800; // Default to 30 minutes if not provided
    const expiresAt = Date.now() + expiresIn * 1000;

    // Store token
    setSearchApiToken(data.accessToken, expiresAt, data.tokenType || 'Bearer');

    return [null, response];
  } catch (error) {
    console.error('Error generating Search API token:', error);
    return [error, null];
  }
};

/**
 * Refreshes the Search API token by generating a new one
 * @returns {Promise<[Error, Object]>} Tuple of [error, response]
 */
export const refreshSearchApiToken = async () => {
  try {
    const [error, response] = await request({
      method: 'post',
      url: searchApiTokenRefreshEndpoint,
      baseURL: backendBaseUrl,
      data: {},
    })
      .then((responseData) => [null, responseData.data])
      .catch((responseError) => [responseError, null]);

    if (error || !response) {
      console.error('Failed to refresh Search API token', error);
      // If refresh fails, try generating a new token
      return generateSearchApiToken();
    }

    // Extract token data from response
    const { data } = response;
    if (!data || !data.accessToken) {
      return [new Error('Invalid token response structure'), null];
    }

    // Calculate expiration timestamp
    const expiresIn = data.expiresIn || 1800;
    const expiresAt = Date.now() + expiresIn * 1000;

    // Store token
    setSearchApiToken(data.accessToken, expiresAt, data.tokenType || 'Bearer');

    return [null, response];
  } catch (error) {
    console.error('Error refreshing Search API token:', error);
    // If refresh fails, try generating a new token
    return generateSearchApiToken();
  }
};

/**
 * Retrieves the Search API token
 * @returns {string|null} The access token or null if not found
 */
export const getSearchApiToken = () => {
  return getCookie(SEARCH_API_TOKEN_KEY) || null;
};

/**
 * Retrieves the Search API token expiration timestamp
 * @returns {number|null} The expiration timestamp or null if not found
 */
export const getSearchApiTokenExpiresAt = () => {
  const expiresAt = getCookie(SEARCH_API_TOKEN_EXPIRES_AT_KEY);
  return expiresAt ? parseInt(expiresAt, 10) : null;
};

/**
 * Checks if the Search API token is expired or will expire soon
 * @returns {boolean} True if token is expired or expiring soon
 */
export const isSearchApiTokenExpired = () => {
  const expiresAt = getSearchApiTokenExpiresAt();
  if (!expiresAt) return true;

  // Check if token is expired or will expire within buffer time
  return Date.now() >= expiresAt - REFRESH_BUFFER_TIME;
};

/**
 * Gets a valid Search API token, refreshing if necessary
 * @returns {Promise<[Error, string]>} Tuple of [error, token]
 */
export const getValidSearchApiToken = async () => {
  let token = getSearchApiToken();

  // If no token or token is expired, refresh it
  if (!token || isSearchApiTokenExpired()) {
    const [error] = await refreshSearchApiToken();
    if (error) {
      return [error, null];
    }
    token = getSearchApiToken();
  }

  if (!token) {
    return [new Error('Failed to obtain Search API token'), null];
  }

  return [null, token];
};

/**
 * Clears the Search API token from storage
 */
export const clearSearchApiToken = () => {
  removeCookie(SEARCH_API_TOKEN_KEY);
  removeCookie(SEARCH_API_TOKEN_EXPIRES_AT_KEY);
  removeCookie(SEARCH_API_TOKEN_TYPE_KEY);
};

/**
 * Sets up automatic token refresh interval
 * @param {number} intervalMs - Interval in milliseconds (default: 30 minutes)
 * @returns {number} The interval ID
 */
export const syncSearchApiTokenWithInterval = (intervalMs = 30 * 60 * 1000) => {
  // Clear existing interval if any
  if (window.searchApiTokenRefreshInterval) {
    clearInterval(window.searchApiTokenRefreshInterval);
  }

  // Set up new interval
  window.searchApiTokenRefreshInterval = setInterval(async () => {
    const token = getSearchApiToken();
    if (token && isSearchApiTokenExpired()) {
      console.log('Refreshing Search API token...');
      await refreshSearchApiToken();
    }
  }, intervalMs);

  return window.searchApiTokenRefreshInterval;
};

/**
 * Clears the Search API token refresh interval
 */
export const unSyncSearchApiTokenWithInterval = () => {
  if (window.searchApiTokenRefreshInterval) {
    clearInterval(window.searchApiTokenRefreshInterval);
    window.searchApiTokenRefreshInterval = null;
  }
};

/**
 * Initializes Search API token on app load
 * @returns {Promise<[Error, Object]>} Tuple of [error, response]
 */
export const initializeSearchApiToken = async () => {
  // Generate initial token
  const [error, response] = await generateSearchApiToken();

  if (!error && response) {
    // Set up refresh interval based on expiration time
    const expiresIn = response?.data?.expiresIn || 1800;
    // Refresh at 80% of expiration time to be safe
    const refreshInterval = Math.floor(expiresIn * 0.8 * 1000);
    syncSearchApiTokenWithInterval(refreshInterval);
  }

  return [error, response];
};
